import { getCookie } from 'cookies-next'
import { cookieKeys } from 'lib/consts/browserStorage'
import { getTokenUserId } from 'lib/helpers/getTokenUserId/getTokenUserId'
import { isSSR } from 'lib/helpers/isSSR/isSSR'
import { getBusinessRootPath } from 'lib/navigation/helpers/getBusinessRootPath/getBusinessRootPath'
import { addAction } from 'lib/tracking/datadog'
import { t } from 'lib/translations/i18next'

let tabUserId: string | null = null

const getCookieUserId = () => getTokenUserId(getCookie(cookieKeys.token))

/**
 * Records the cookie's user as the one this tab acts as. Only the first call per page load counts: a
 * stale tab may do nothing until another tab has already swapped the cookie, and a later capture
 * would then adopt the new user.
 */
export const rememberTabUser = (): void => {
  if (!isSSR()) {
    tabUserId ??= getCookieUserId()
  }
}

/** Records the user this tab now acts as, for a tab that swaps the token cookie itself. */
export const setTabUser = (id: typeof tabUserId): void => {
  tabUserId = id
}

interface RedirectIfTabUserSwitchedParams {
  trigger: 'graphql' | 'navigation'
  /** The operation name or URL that noticed the switch, for the RUM action. */
  target?: string
}

/**
 * The token cookie is shared by every tab, so signing in as someone else in another tab swaps the
 * user under this one while the page still shows the previous user's business. When that has
 * happened, reloads into the new user's first business with a warning and returns true; the caller
 * must then drop whatever it was about to do.
 */
export const redirectIfTabUserSwitched = ({ trigger, target }: RedirectIfTabUserSwitchedParams): boolean => {
  if (isSSR()) {
    return false
  }

  const cookieUserId = getCookieUserId()

  if (!tabUserId || !cookieUserId || cookieUserId === tabUserId) {
    return false
  }

  addAction('cross_tab_user_switch', { trigger, target })
  window.location.assign(
    getBusinessRootPath(null, {
      search: new URLSearchParams({ warning: t('authSession::switchedInAnotherTab') }).toString(),
    }),
  )

  return true
}
